import { useApi } from '@hero/context';
import type {
  UseBillingAdminCancelApi,
  UseBillingAdminListApi,
  UseBillingApi,
  UseBillingListApi,
  UseBillingTransactionAdminRefundApi,
  UseBillingTransactionApi,
  UseBillingTransactionByGatewayPaymentIdApi,
  UseBillingTransactionListApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/** Single Billing by id. Disabled while `id` is undefined. */
export const useBillingApi: UseBillingApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'billing',
    apiFn: api.sales.billing.getOne,
    query: undefined,
    singleIdQuery: id,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/** Paginated list of Billings scoped to the current org. */
export const useBillingListApi: UseBillingListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'billing',
    apiFn: api.sales.billing.getMany,
    query,
    apiConfig,
  });
};

/** Admin: paginated list of Billings across all orgs. */
export const useBillingAdminListApi: UseBillingAdminListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'billing',
    apiFn: api.sales.billing.adminGetAll,
    query,
    apiConfig,
  });
};

/** Admin: cancel a draft / pending Billing. */
export const useBillingAdminCancelApi: UseBillingAdminCancelApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'billing',
    apiFn: api.sales.billing.adminCancel,
    apiConfig,
  });
};

/** Single BillingTransaction by id. */
export const useBillingTransactionApi: UseBillingTransactionApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'billingTransaction',
    apiFn: api.sales.billingTransaction.getOne,
    query: undefined,
    singleIdQuery: id,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/** Paginated list of BTxs scoped by org / billing. */
export const useBillingTransactionListApi: UseBillingTransactionListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'billingTransaction',
    apiFn: api.sales.billingTransaction.getMany,
    query,
    apiConfig,
  });
};

/** Admin: refund against a Billing's last successful charge. */
export const useBillingTransactionAdminRefundApi: UseBillingTransactionAdminRefundApi =
  apiConfig => {
    const api = useApi();
    return useMutationAPI({
      entity: 'billingTransaction',
      apiFn: api.sales.billingTransaction.adminRefund,
      apiConfig,
    });
  };

/**
 * Lookup a BTx + parent Billing by Moyasar payment id. Used by the
 * payment-confirmation return page after Moyasar redirects the user
 * back with `?id=<moyasarPaymentId>` appended to the URL.
 *
 * The hook is disabled while `moyasarPaymentId` is undefined and the
 * page is responsible for refetching while waiting on a terminal
 * status (we don't bake the polling interval into the hook so other
 * consumers can pick their own cadence).
 */
export const useBillingTransactionByGatewayPaymentIdApi: UseBillingTransactionByGatewayPaymentIdApi =
  (moyasarPaymentId, apiConfig) => {
    const api = useApi();
    return useQueryAPI({
      entity: 'billingTransaction',
      apiFn: api.sales.billingTransaction.byGatewayPaymentId,
      query: moyasarPaymentId ? { id: moyasarPaymentId } : undefined,
      apiConfig,
      isEnabled: () => !!moyasarPaymentId,
    });
  };
